import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import FirstEquityLogo from './AdditionLogo';

export default function Navigation() {
  const { currentUser, userRole, logout } = useAuth();
  const navigate = useNavigate();
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showHelpTooltip, setShowHelpTooltip] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  // Mock notifications data
  const notifications = [
    {
      id: 1,
      type: 'success',
      message: 'A new login session started',
      time: 'Now'
    },
    {
      id: 2,
      type: 'info',
      message: 'New statement is available for viewing',
      time: '1 day ago'
    },
    {
      id: 3,
      type: 'warning',
      message: 'Please verify your email address',
      time: '2 days ago'
    }
  ];

  // Handle scroll events
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleNavigation = (path, sectionId) => {
    if (window.location.pathname !== '/dashboard') {
      navigate('/dashboard');
      // Wait for navigation to complete before scrolling
      setTimeout(() => scrollToSection(sectionId), 100);
    } else {
      scrollToSection(sectionId);
    }
  };

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Products', path: '/products' },
    { name: 'Contact', path: '/contact' }
  ];

  const navigation = [
    { 
      name: 'Dashboard', 
      action: () => navigate('/dashboard'), 
      icon: '🏠' 
    },
    { 
      name: 'Transfers', 
      action: () => handleNavigation('/dashboard', 'transfer-section'), 
      icon: '↔️' 
    },
    { 
      name: 'Cards', 
      action: () => {
        if (window.location.pathname !== '/dashboard') {
          navigate('/dashboard#quick-actions-cards');
        } else {
          window.location.hash = 'quick-actions-cards';
        }
      },
      icon: '💳' 
    },
    { 
      name: 'Bill Pay', 
      action: () => {
        if (window.location.pathname !== '/dashboard') {
          navigate('/dashboard#quick-actions-billpay');
        } else {
          window.location.hash = 'quick-actions-billpay';
        }
      },
      icon: '💰' 
    },
  ];

  return (
    <nav className={`fixed top-0 left-0 right-0 bg-white border-b border-gray-100 z-50 transition-all duration-200
      ${isScrolled ? 'shadow-md' : 'shadow-sm'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-24 md:h-28">
          {/* Logo and Navigation Links */}
          <div className="flex">
            {/* Logo */}
            <div className="flex-shrink-0 flex items-center px-3">
              <FirstEquityLogo />
            </div>

            {/* Navigation Links */}
            <div className="hidden md:ml-12 md:flex md:items-center md:space-x-1">
              {currentUser ? (
                // Dashboard navigation
                navigation.map((item) => (
                  <button
                    key={item.name}
                    onClick={item.action}
                    className="px-3 py-2 rounded-lg text-gray-600 hover:text-primary-600 hover:bg-primary-50
                      transition-all duration-200 flex items-center space-x-2 group"
                  >
                    <span className="text-lg group-hover:scale-110 transition-transform duration-200">{item.icon}</span>
                    <span className="font-medium">{item.name}</span>
                  </button>
                ))
              ) : (
                // Public navigation
                navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className="px-3 py-2 rounded-lg text-gray-600 hover:text-primary-600 hover:bg-primary-50
                      transition-all duration-200 font-medium"
                  >
                    {link.name}
                  </Link>
                ))
              )}
            </div>
          </div>

          {/* Right side menu */}
          <div className="flex items-center space-x-4">
            {/* Help Button */}
            <div className="relative">
              <button
                className="p-2 text-gray-500 hover:text-gray-600 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                onMouseEnter={() => setShowHelpTooltip(true)}
                onMouseLeave={() => setShowHelpTooltip(false)}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </button>
              
              {/* Help Tooltip */}
              {showHelpTooltip && (
                <div className="absolute right-0 mt-2 w-72 bg-white rounded-xl shadow-lg border border-gray-100 p-4 transform transition-all duration-200 z-50">
                  <div className="flex items-center space-x-3 text-gray-600">
                    <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <p className="text-sm">Need assistance? Contact us:</p>
                      <a 
                        href="mailto:support@firstequitycu.com"
                        className="text-sm font-medium text-primary-600 hover:text-primary-700"
                      >
                        support@firstequitycu.com
                      </a>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Notifications Button */}
            <div className="relative">
              <button
                className="p-2 text-gray-500 hover:text-gray-600 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                onClick={() => setShowNotifications(!showNotifications)}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
                {/* Notification Badge */}
                <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full"></span>
              </button>

              {/* Notifications Popover */}
              {showNotifications && (
                <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden transform transition-all duration-200 z-50">
                  <div className="p-4 border-b border-gray-100">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-semibold text-gray-900">Notifications</h3>
                      <span className="text-xs font-medium text-primary-600 hover:text-primary-700 cursor-pointer">
                        Mark all as read
                      </span>
                    </div>
                  </div>
                  
                  <div className="divide-y divide-gray-100 max-h-[300px] overflow-y-auto">
                    {notifications.map((notification) => (
                      <div key={notification.id} className="p-4 hover:bg-gray-50 transition-colors duration-200">
                        <div className="flex items-start space-x-3">
                          <div className={`w-2 h-2 mt-2 rounded-full flex-shrink-0
                            ${notification.type === 'success' ? 'bg-green-500' :
                              notification.type === 'warning' ? 'bg-yellow-500' :
                              'bg-primary-500'}`}
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-gray-900">{notification.message}</p>
                            <p className="text-xs text-gray-500 mt-1">{notification.time}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="p-4 bg-gray-50 border-t border-gray-100">
                    
                  </div>
                </div>
              )}
            </div>
            
            {/* Profile Dropdown */}
            <div className="relative">
              <button
                onClick={() => setIsProfileOpen(!isProfileOpen)}
                className="flex items-center space-x-3 px-3 py-2 rounded-lg text-gray-700 
                  hover:bg-primary-50 transition-all duration-200"
              >
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-500 to-primary-600 
                  flex items-center justify-center text-white shadow-sm">
                  {currentUser?.email?.[0].toUpperCase() || 'U'}
                </div>
                <div className="hidden md:block text-left">
                  <p className="text-sm font-medium">{currentUser?.email?.split('@')[0]}</p>
                  <p className="text-xs text-gray-500">Personal Account</p>
                </div>
                <svg className="hidden md:block w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Profile Dropdown Menu */}
              {isProfileOpen && (
                <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-lg py-2 z-50
                  ring-1 ring-black ring-opacity-5 transform transition-all duration-200">
                  <div className="px-4 py-2 border-b border-gray-100">
                    <p className="text-sm font-medium text-gray-900">{currentUser?.email}</p>
                    <p className="text-xs text-gray-500">Personal Banking</p>
                  </div>
                  <div className="px-2 py-2 space-y-1">
                    {userRole === 'admin' && (
                      <button 
                        className="w-full flex items-center text-left px-2 py-2 text-sm text-gray-700 hover:bg-primary-50 
                          rounded-lg transition-colors duration-200" 
                        onClick={() => navigate('/admin/reset-transfers')}
                      >
                        <svg className="w-5 h-5 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Reset Transfer Counts
                      </button>
                    )}
                    <button 
                      className="w-full flex items-center text-left px-2 py-2 text-sm text-gray-700 hover:bg-primary-50 
                        rounded-lg transition-colors duration-200"
                    >
                      <svg className="w-5 h-5 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      Profile Settings
                    </button>
                    <button 
                      className="w-full flex items-center text-left px-2 py-2 text-sm text-gray-700 hover:bg-primary-50 
                        rounded-lg transition-colors duration-200"
                    >
                      <svg className="w-5 h-5 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      Account Settings
                    </button>
                    <button 
                      className="w-full flex items-center text-left px-2 py-2 text-sm text-red-600 hover:bg-red-50 
                        rounded-lg transition-colors duration-200" 
                      onClick={handleLogout}
                    >
                      <svg className="w-5 h-5 mr-2 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                      Sign out
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="p-2 rounded-lg text-gray-600 hover:text-primary-600 hover:bg-primary-50 
                  transition-all duration-200"
              >
                {isMobileMenuOpen ? (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden py-3 border-t border-gray-100">
            <div className="space-y-1 px-2">
              {currentUser ? (
                // Dashboard mobile navigation
                <>
                  {navigation.map((item) => (
                    <button
                      key={item.name}
                      onClick={item.action}
                      className="w-full flex items-center space-x-2 px-3 py-2 text-gray-700 hover:text-primary-600 
                        hover:bg-primary-50 rounded-lg transition-all duration-200"
                    >
                      <span className="text-xl">{item.icon}</span>
                      <span className="font-medium">{item.name}</span>
                    </button>
                  ))}
                  <div className="h-px bg-gray-100 my-2"></div>
                </>
              ) : (
                // Public mobile navigation
                <>
                  {navLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      className="block px-3 py-2 text-gray-700 hover:text-primary-600 
                        hover:bg-primary-50 rounded-lg transition-all duration-200"
                    >
                      {link.name}
                    </Link>
                  ))}
                  <div className="h-px bg-gray-100 my-2"></div>
                </>
              )}
              <button className="w-full flex items-center space-x-2 px-3 py-2 text-gray-700 hover:text-primary-600 
                hover:bg-primary-50 rounded-lg transition-all duration-200">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                    d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="font-medium">Help</span>
              </button>
              <button
                onClick={handleLogout}
                className="w-full flex items-center space-x-2 px-3 py-2 text-red-600 hover:bg-red-50 
                  rounded-lg transition-all duration-200"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                    d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                <span className="font-medium">Sign out</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}